import { useRef, useCallback, useEffect } from 'react';

/**
 * Hook to enable swipe-down-to-close functionality for modals on mobile
 * @param {Function} onClose - Function to call when swipe threshold is reached
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Minimum distance to trigger close (default: 100)
 * @param {boolean} options.enabled - Whether swipe is enabled (default: true)
 * @returns {Object} - Ref and handlers to attach to the swipeable element
 */
export function useSwipeToClose(onClose, options = {}) {
  const { threshold = 100, enabled = true } = options;
  
  const elementRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  
  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    
    // Only start drag from near the top of the modal (drag handle area)
    const element = elementRef.current;
    if (!element) return;
    
    const touch = e.touches[0];
    const rect = element.getBoundingClientRect();
    const touchYRelative = touch.clientY - rect.top;
    
    // Only allow drag from top 60px of modal (header/handle area)
    if (touchYRelative > 60) return;
    
    startY.current = touch.clientY;
    currentY.current = touch.clientY;
    isDragging.current = true;
    
    // Add visual feedback
    element.style.transition = 'none';
  }, [enabled]);
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current || !enabled) return;
    
    const element = elementRef.current;
    if (!element) return;
    
    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;
    
    // Only allow downward swipe
    if (deltaY > 0) {
      element.style.transform = `translateY(${deltaY}px)`;
      element.style.opacity = Math.max(0.5, 1 - deltaY / 300);
    }
  }, [enabled]);
  
  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current || !enabled) return;
    
    const element = elementRef.current;
    if (!element) return;
    
    const deltaY = currentY.current - startY.current;
    
    isDragging.current = false;
    element.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
    
    if (deltaY > threshold) {
      // Swipe threshold reached - close the modal
      element.style.transform = 'translateY(100%)';
      element.style.opacity = '0';
      setTimeout(() => {
        onClose();
      }, 200);
    } else {
      // Reset position
      element.style.transform = 'translateY(0)';
      element.style.opacity = '1';
    }
  }, [enabled, threshold, onClose]);
  
  // Attach touch handlers
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;
    
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, enabled]);
  
  return { ref: elementRef };
}

export default useSwipeToClose;
